/**
 * Helper function for abbreviating large numbers (i.e. 1000 => 1k).
 */
export const abbrevNumber = (val: number): string => Intl.NumberFormat('en-US', { notation: 'compact' }).format(val);

/**
 * Helper function for formatting large numbers for readability (i.e. 100000 => 100,000).
 */
export const prettyNumber = (val: number): string => Intl.NumberFormat('en-US').format(val);

const FORMAT_COUNT_LIMIT_DEFAULT = 10_000;

/**
 * If the count is >= the limit, we display it like "10k" instead of "10,000".
 * If the count is less than the limit, we display it as is, but with commas if required (ie: 1,234 instead of 1234).
 *
 * @param {number} count - The number to format.
 * @param {number} [limit] - Limit before the number is abbreviated. (@default 10_000)
 */
export const formatCount = (count: number, limit: number = FORMAT_COUNT_LIMIT_DEFAULT) =>
  count >= limit ? abbrevNumber(count) : prettyNumber(count);

const pr = new Intl.PluralRules('en-US', { type: 'ordinal' });

const suffixes = new Map([
  ['one', 'st'],
  ['two', 'nd'],
  ['few', 'rd'],
  ['other', 'th'],
]);

/**
 * Given a number, add the appropriate ordinal suffix.
 * Taken from https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/PluralRules/PluralRules.
 * @example
 * formatOrdinals(0); // '0th'
 * formatOrdinals(1); // '1st'
 * formatOrdinals(2); // '2nd'
 * formatOrdinals(3); // '3rd'
 * formatOrdinals(4); // '4th'
 * formatOrdinals(11); // '11th'
 * formatOrdinals(21); // '21st'
 * formatOrdinals(42); // '42nd'
 * formatOrdinals(103); // '103rd'
 * @param val - The number to format.
 * @returns The formatted number with the appropriate ordinal suffix.
 */
export const formatOrdinals = (val: number): string => {
  const rule = pr.select(val);
  const suffix = suffixes.get(rule);
  return `${val}${suffix}`;
};
